import { useTranslation } from "react-i18next";
import React, { memo, useState, useCallback, Suspense, useContext, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
	Table,
	Typography,
	Tag,
	Tooltip,
	Avatar,
	Button,
	message,
	PlusCircleFilled,
	Divider,
	Checkbox,
	Dropdown,
	EditOutlined,
	DeleteOutlined,
} from "src/assets/imports/antdComponents";
import { setSelectedStudy, updateStudyStar } from "../../states/studiesSlice";
import { deleteFolder, editStudyStar, moveToFolder, getFolderDeleteOptions } from "../../serviceHelper";
import { formatDeleteMessage, formatMessage, getInitials, getStudyTypeDisplayName, transformDateTime } from "../../transformer";
import type { StudyTypeIconDescriptor } from "../../transformer";
import StudyTypeIcon from "../StudyTypeIcon";
import {
	CollabCardRenderingFromType,
	FeatureKeysType,
	ICollabTableProps,
	IFolderData,
	IResponse,
	IStudyData,
	storyType,
	SupportedLanguageType,
	userAccessType,
	ModuleTypes,
	ActionTypes,
} from "../../models/interfaces";
import { truncateText } from "src/pages/ManageStudies/transformer";
import { useFeatures } from "src/config/featureManager";
import { useActionTracking } from "src/components/UserMonitoring/useActionTracking";
import Folder from "public/icon/Folder.svg";
import FolderDeleteModal from "../FolderDeleteModal";
import { folderDeleteMessages } from "src/config/dataConfig";
import "./style.css";
import { RootState } from "src/store";

const SelectionContext = React.createContext<Record<string, boolean>>({});

const AddCollaboratorModal = React.lazy(() => import("../AddCollaborator"));
const MoveFolderModal = React.lazy(() => import("../FolderModal"));

const ROW_HEIGHT = 72;
const INITIAL_ROWS = 50;
const OVERSCAN = 5;

function useVirtualRows<T>(
	items: T[],
	anchorRef: React.RefObject<HTMLElement>,
	rowHeight: number = ROW_HEIGHT,
	dataReady: boolean = true,
) {
	const [range, setRange] = useState({ start: 0, end: Math.min(items.length, INITIAL_ROWS) });
	const scrollContainerRef = useRef<HTMLElement | null>(null);
	const tableTopOffsetRef = useRef(0);
	const itemCountRef = useRef(items.length);
	const rafRef = useRef<number | null>(null);

	useEffect(() => {
		itemCountRef.current = items.length;
	}, [items.length]);

	const measure = useCallback(() => {
		const container = scrollContainerRef.current;
		const anchor = anchorRef.current;
		if (container && anchor) {
			const containerRect = container.getBoundingClientRect();
			const anchorRect = anchor.getBoundingClientRect();
			tableTopOffsetRef.current = anchorRect.top - containerRect.top + container.scrollTop;
		}
	}, [anchorRef]);

	const computeRange = useCallback(() => {
		const count = itemCountRef.current;
		if (count <= INITIAL_ROWS) {
			setRange({ start: 0, end: count });
			return;
		}
		const container = scrollContainerRef.current;
		if (!container) return;
		const { scrollTop, clientHeight } = container;
		const offset = tableTopOffsetRef.current;
		const firstVisible = Math.floor((scrollTop - offset) / rowHeight);
		const lastVisible = Math.ceil((scrollTop - offset + clientHeight) / rowHeight);
		// Clamp to valid bounds so a stale scrollTop (e.g. right after a filter
		// shrinks the list) can never produce start > count, which would render
		// an empty slice with huge paddingTop and look like a missing list.
		const rawStart = Math.max(0, firstVisible - OVERSCAN);
		const rawEnd = Math.min(count, lastVisible + OVERSCAN);
		const start = Math.min(rawStart, Math.max(0, count - 1));
		const end = Math.max(start, rawEnd);
		setRange((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
	}, [rowHeight]);

	const onScroll = useCallback(() => {
		if (rafRef.current !== null) return;
		rafRef.current = requestAnimationFrame(() => {
			rafRef.current = null;
			computeRange();
		});
	}, [computeRange]);

	useEffect(() => {
		if (!scrollContainerRef.current) {
			let el = anchorRef.current?.parentElement;
			while (el && !/auto|scroll/.test(getComputedStyle(el).overflowY)) el = el.parentElement;
			scrollContainerRef.current = el || null;
		}
		const el = scrollContainerRef.current;
		if (el) {
			measure();
			el.addEventListener("scroll", onScroll, { passive: true });
			window.addEventListener("resize", measure, { passive: true });
		}
		return () => {
			el?.removeEventListener("scroll", onScroll);
			window.removeEventListener("resize", measure);
			if (rafRef.current !== null) {
				cancelAnimationFrame(rafRef.current);
				rafRef.current = null;
			}
		};
	}, [onScroll, measure]);

	useEffect(() => {
		if (!dataReady) return;
		// When the list length changes (filter / sort / refresh), the previously
		// captured scrollTop is no longer meaningful for the new list. Reset to
		// top first so computeRange below reads scrollTop=0 instead of a stale
		// value that points past the end of the shrunken list.
		if (scrollContainerRef.current && scrollContainerRef.current.scrollTop !== 0) {
			scrollContainerRef.current.scrollTop = 0;
		}
		measure();
		computeRange();
	}, [items.length, computeRange, measure, dataReady]);

	if (!dataReady) {
		return {
			startIndex: 0,
			endIndex: items.length,
			paddingTop: 0,
			paddingBottom: 0,
			scrollContainerRef,
		};
	}

	return {
		startIndex: range.start,
		endIndex: range.end,
		paddingTop: items.length > INITIAL_ROWS ? range.start * rowHeight : 0,
		paddingBottom: items.length > INITIAL_ROWS ? (items.length - range.end) * rowHeight : 0,
		scrollContainerRef,
	};
}

const checkboxCellAreEqual = (
	prev: { record: any; isFolderEnabled: boolean; onChange: (id: string, checked: boolean) => void },
	next: { record: any; isFolderEnabled: boolean; onChange: (id: string, checked: boolean) => void },
) =>
	prev.isFolderEnabled === next.isFolderEnabled &&
	prev.record.study_id === next.record.study_id &&
	prev.record.user_access_type === next.record.user_access_type &&
	prev.record.isFolder === next.record.isFolder &&
	prev.onChange === next.onChange;

const CheckboxCell = memo(
	({ record, isFolderEnabled, onChange }: { record: any; isFolderEnabled: boolean; onChange: (studyId: string, checked: boolean) => void }) => {
		const { t } = useTranslation();
		const selectedStudiesMap = useContext(SelectionContext);
		const isChecked = !!selectedStudiesMap[record.study_id];
		if (!isFolderEnabled) return null;
		const hasFullAccess = record?.user_access_type === userAccessType.edit;
		const isFolder = record.isFolder;
		return (
			<Tooltip
				getPopupContainer={(trigger) => trigger.closest(".studies-page-container") ?? document.body}
				title={isFolder ? t("Feature is disabled for collection") : !hasFullAccess ? t("Edit access is required") : ""}>
				<Checkbox
					checked={isChecked}
					disabled={isFolder || !hasFullAccess}
					onChange={(e) => {
						e.stopPropagation();
						onChange(record.study_id, e.target.checked);
					}}
					onClick={(e) => e.stopPropagation()}
				/>
			</Tooltip>
		);
	},
	checkboxCellAreEqual,
);

const TagsCell = memo(({ record }: { record: any }) => {
	const { t } = useTranslation();
	const selectedStudiesMap = useContext(SelectionContext);
	const isRowSelected = !!selectedStudiesMap[record.study_id];
	const maxVisibleTags = 3;
	const tags = record.isFolder ? record.folder_tags : record.custom_tags;
	const visibleTags = tags?.slice(0, maxVisibleTags) || [];
	const extraTagsCount = (tags?.length ?? 0) - maxVisibleTags > 0 ? (tags?.length ?? 0) - maxVisibleTags : 0;
	return (
		<div className="collab-table-custom-tags flex items-center">
			{visibleTags.map((tag: string, index: number) => (
				<Tag key={`${tag}-${index}`} className="collab-table-study-tag mr-2" title={tag}>
					{truncateText(tag, 10)}
				</Tag>
			))}
			{extraTagsCount > 0 && (
				<Tooltip
					getPopupContainer={(trigger) => trigger.closest(".studies-page-container") ?? document.body}
					overlayClassName={isRowSelected ? "selected-row-tags-tooltip" : ""}
					title={
						<div className="flex gap-2 flex-wrap">
							{tags?.slice(maxVisibleTags).map((tag: string, index: number) => (
								<Tag key={`${tag}-${index}`} className="collab-table-study-tag m-1" title={tag}>
									{truncateText(tag, 40)}
								</Tag>
							))}
						</div>
					}>
					<Tag className="collab-table-study-tag mr-2">
						+ {extraTagsCount} {t("more")}
					</Tag>
				</Tooltip>
			)}
		</div>
	);
});

const CollabTable: React.FC<ICollabTableProps> = ({
	folders = [],
	allFolders,
	studies,
	activeStudyId,
	selectedCardIndex,
	onSelect,
	onEditStudy,
	renderedFrom,
	loading = false,
	onFolderNavigate,
	setBackHandler,
	onEditFolder,
	onRefreshStudies,
	allUntrackedStudies,
	setFolderClickHandler,
	currentFolder,
	fetchAllStudies,
	selectedStudyPageTab,
	onHasSelectedStudiesChange,
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { isFeatureAvailable } = useFeatures();

	const allStudyTypes = useSelector((state: any) => state.studiesSlice.allStudyTypes);

	const [deletingStudyId, setDeletingStudyId] = useState<string | null>(null);
	const [starLoadingMap, setStarLoadingMap] = useState<Record<string, boolean>>({});
	const [isAddCollabModalOpen, setIsAddCollabModalOpen] = useState(false);
	const [collabModalKey, setCollabModalKey] = useState(0);
	const [selectedStudyLocal, setSelectedStudyLocal] = useState<IStudyData | null>(null);

	const [selectedStudiesMap, setSelectedStudiesMap] = useState<Record<string, boolean>>({});
	const selectedStudiesMapRef = useRef<Record<string, boolean>>({});
	useEffect(() => {
		selectedStudiesMapRef.current = selectedStudiesMap;
	}, [selectedStudiesMap]);

	const [currentView, setCurrentView] = useState<"list" | "folder_detail">("list");
	const [selectedFolder, setSelectedFolder] = useState<IFolderData | null>(null);
	const [moreMenuActiveFolderId, setMoreMenuActiveFolderId] = useState<string | null>(null);
	const [isMoveFolderModalOpen, setIsMoveFolderModalOpen] = useState(false);
	const [showMoveButtonHighlight, setShowMoveButtonHighlight] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [selectedFolderToDelete, setSelectedFolderToDelete] = useState<IFolderData | null>(null);
	const [deleteOptions, setDeleteOptions] = useState<any[]>([]);
	const [loadingDeleteOptions, setLoadingDeleteOptions] = useState(false);
	const [deletingFolder, setDeletingFolder] = useState(false);

	const tbodyAnchorRef = useRef<HTMLDivElement>(null);
	const isStudyUpsert = isFeatureAvailable(FeatureKeysType.StudyUpsert);
	const isMultiLingualSupportEnabled = isFeatureAvailable(FeatureKeysType.MultiLingualSupport);
	const { trackAction } = useActionTracking(ModuleTypes.Studies);
	const isFolderUpsert = isFeatureAvailable(FeatureKeysType.FolderUpsert);
	const isFolderEnabled = isFeatureAvailable(FeatureKeysType.EnableFolder);
	const activeFeatures = useSelector((state: RootState) => state.askKuralSlice.activeFeatures);
	const featureName = activeFeatures?.find((ele) => ele.feature_key === FeatureKeysType.EnableFolder)?.feature_name;

	const fetchAllStudiesRef = useRef(fetchAllStudies);
	const selectedStudyPageTabRef = useRef(selectedStudyPageTab);
	const onFolderNavigateRef = useRef(onFolderNavigate);
	const onEditFolderRef = useRef(onEditFolder);
	const onRefreshStudiesRef = useRef(onRefreshStudies);
	const onHasSelectedStudiesChangeRef = useRef(onHasSelectedStudiesChange);

	useEffect(() => {
		fetchAllStudiesRef.current = fetchAllStudies;
	}, [fetchAllStudies]);
	useEffect(() => {
		selectedStudyPageTabRef.current = selectedStudyPageTab;
	}, [selectedStudyPageTab]);
	useEffect(() => {
		onFolderNavigateRef.current = onFolderNavigate;
	}, [onFolderNavigate]);
	useEffect(() => {
		onEditFolderRef.current = onEditFolder;
	}, [onEditFolder]);
	useEffect(() => {
		onRefreshStudiesRef.current = onRefreshStudies;
	}, [onRefreshStudies]);
	useEffect(() => {
		onHasSelectedStudiesChangeRef.current = onHasSelectedStudiesChange;
	}, [onHasSelectedStudiesChange]);

	const selectedCount = useMemo(() => Object.keys(selectedStudiesMap).length, [selectedStudiesMap]);

	useEffect(() => {
		onHasSelectedStudiesChangeRef.current?.(selectedCount > 0);
	}, [selectedCount]);

	useEffect(() => {
		if (selectedCount > 0) {
			setShowMoveButtonHighlight(true);
			const timer = setTimeout(() => setShowMoveButtonHighlight(false), 3000);
			return () => clearTimeout(timer);
		} else {
			setShowMoveButtonHighlight(false);
		}
	}, [selectedCount]);

	const showAddCollabModal = useCallback((e: React.MouseEvent, record: IStudyData) => {
		e.stopPropagation();
		setCollabModalKey((k) => k + 1);
		setIsAddCollabModalOpen(true);
		setSelectedStudyLocal(record);
	}, []);

	const handleOk = useCallback(() => setIsAddCollabModalOpen(false), []);
	const handleCancel = useCallback(() => setIsAddCollabModalOpen(false), []);

	const handleManageClick = useCallback(
		(e: React.MouseEvent, study: IStudyData) => {
			e.stopPropagation();
			dispatch(
				setSelectedStudy({
					study_id: study.study_id,
					study_name: study.study_name,
					study_tags: study.custom_tags,
					study_collaborators: study.collaborators,
					insights_id: study.active_insights_id,
					access_type: study.user_access_type,
					created_by: study.version_history?.created_by,
					study_type: study.study_type,
					insights_available: study.insights_available,
					is_date_mapped: study.is_date_mapped ?? false,
					quant_tabs: study?.quant_tabs,
					study_language: study?.study_language ?? { name: "English", code: SupportedLanguageType.English },
					last_updated_at: study?.version_history?.modified_dt,
					refresh_insights_id: study?.refresh_insights_id,
				}),
			);
			navigate("/manage-studies");
		},
		[dispatch, navigate],
	);

	const handleInsightsClick = useCallback(
		(e: React.MouseEvent, study: IStudyData) => {
			e.stopPropagation();
			dispatch(
				setSelectedStudy({
					study_id: study.study_id,
					study_name: study.study_name,
					study_tags: study.custom_tags,
					study_collaborators: study.collaborators,
					insights_id: study.active_insights_id,
					access_type: study.user_access_type,
					created_by: study.version_history?.created_by,
					study_type: study.study_type,
					insights_available: study.insights_available,
					is_date_mapped: study.is_date_mapped ?? false,
					study_language: study?.study_language ?? { name: "English", code: SupportedLanguageType.English },
					quant_tabs: study?.quant_tabs,
					last_updated_at: study?.version_history?.modified_dt,
					refresh_insights_id: study?.refresh_insights_id,
				}),
			);
			navigate("/insights", { state: { previousPath: window.location.pathname, pathHistory: [window.location.pathname] } });
		},
		[dispatch, navigate],
	);

	const handleToggleStar = useCallback(
		async (e: React.MouseEvent, study: IStudyData) => {
			e.preventDefault();
			e.stopPropagation();
			const studyId = study.study_id;
			if (starLoadingMap[studyId]) return;
			setStarLoadingMap((prev) => ({ ...prev, [studyId]: true }));
			const isStarred = study?.is_star ?? false;
			try {
				const response: IResponse = await editStudyStar({ entity_id: studyId, is_star: !isStarred });
				if (response?.message) {
					trackAction(ActionTypes.STARRED_UPDATED, {
						study_id: study?.study_id,
						study_name: study?.study_name,
						study_type: study?.study_type,
						feature: true,
						type: undefined,
					});
					dispatch(updateStudyStar({ study_id: studyId, star: !isStarred }));
					setSelectedFolder((prev) => (prev ? { ...prev, studies: prev.studies.map((s) => (s.study_id === studyId ? { ...s, is_star: !isStarred } : s)) } : prev));
					message.success(t(response.message));
					if (isStarred) fetchAllStudiesRef.current(selectedStudyPageTabRef.current);
				}
			} catch (error) {
				console.error("Error toggling star:", error);
			} finally {
				setStarLoadingMap((prev) => ({ ...prev, [studyId]: false }));
			}
		},
		// starLoadingMap omitted — functional setState already reads the latest value.
		[dispatch, t, trackAction],
	);

	const handleCheckboxChange = useCallback((studyId: string, checked: boolean) => {
		setSelectedStudiesMap((prev) => {
			if (checked) return { ...prev, [studyId]: true };
			const next = { ...prev };
			delete next[studyId];
			return next;
		});
	}, []);

	const handleSelectAll = useCallback(
		(checked: boolean) => {
			if (checked) {
				const newMap: Record<string, boolean> = {};
				const source = currentView === "folder_detail" && selectedFolder ? selectedFolder.studies : allUntrackedStudies;
				source.forEach((s) => {
					if (s.user_access_type === userAccessType.edit) newMap[s.study_id] = true;
				});
				setSelectedStudiesMap(newMap);
			} else {
				setSelectedStudiesMap({});
			}
		},
		[currentView, selectedFolder, allUntrackedStudies],
	);

	const handleFolderClick = useCallback((folder: IFolderData) => {
		setSelectedStudiesMap({});
		setSelectedFolder(folder);
		setCurrentView("folder_detail");
		onFolderNavigateRef.current?.(folder);
	}, []);

	const handleBackToList = useCallback(() => {
		setSelectedStudiesMap({});
		setCurrentView("list");
		setSelectedFolder(null);
		onFolderNavigateRef.current?.(null);
	}, []);

	useEffect(() => {
		setBackHandler?.(handleBackToList);
	}, [setBackHandler, handleBackToList]);

	const handleFolderRename = useCallback((folder: IFolderData) => {
		setMoreMenuActiveFolderId(null);
		onEditFolderRef.current?.(folder);
	}, []);

	const fetchDeleteOptions = useCallback(
		async (folder: IFolderData) => {
			setLoadingDeleteOptions(true);
			setMoreMenuActiveFolderId(null);
			try {
				const response: IResponse = await getFolderDeleteOptions(folder.folder_id);
				if (response?.data) {
					const transformedOptions = response.data.folder_actions.map((action: any) => ({
						button_text: action.action_label,
						button_value: action.action_id,
						button_description: formatDeleteMessage(folderDeleteMessages[action.message_type] || action.message || "", response.data.studies_count, t, featureName),
						button_status: action.is_enabled,
						message_type: action.message_type,
					}));
					setDeleteOptions(transformedOptions);
					setSelectedFolderToDelete(folder);
					setIsDeleteModalOpen(true);
				} else {
					message.error(t("Failed to fetch delete options"));
				}
			} catch (error) {
				console.error("Error fetching delete options:", error);
				message.error(t("Failed to fetch delete options"));
			} finally {
				setLoadingDeleteOptions(false);
			}
		},
		[t, featureName],
	);

	const handleFolderDelete = useCallback(
		async (buttonValue: string) => {
			if (!selectedFolderToDelete) return;
			setDeletingFolder(true);
			try {
				const response: IResponse = await deleteFolder(selectedFolderToDelete.folder_id, buttonValue);
				if (response?.message) {
					message.success(t(response.message));
					setIsDeleteModalOpen(false);
					setSelectedFolderToDelete(null);
					setDeleteOptions([]);
					onRefreshStudiesRef.current?.();
				} else {
					message.error(response?.message || t("Failed to delete folder"));
				}
			} catch (error) {
				console.error("Error deleting folder:", error);
			} finally {
				setDeletingFolder(false);
			}
		},
		[selectedFolderToDelete, t],
	);

	const handleDeleteModalCancel = useCallback(() => {
		setIsDeleteModalOpen(false);
		setSelectedFolderToDelete(null);
		setDeleteOptions([]);
	}, []);

	const handleMoveToFolder = useCallback(
		async (targetFolderId: string | null) => {
			try {
				const studyIds = Object.keys(selectedStudiesMapRef.current);
				const response: IResponse = await moveToFolder(targetFolderId, studyIds);
				if (response?.message) {
					message.success(t(response.message));
					setSelectedStudiesMap({});
					setIsMoveFolderModalOpen(false);
					onRefreshStudiesRef.current?.(targetFolderId);
				} else {
					message.error(response?.message || t("Failed to move folder"));
				}
			} catch (error) {
				console.error("Error moving studies:", error);
			}
		},
		[t],
	);

	useEffect(() => {
		if (currentView !== "folder_detail") return;
		setSelectedFolder((prev) => (prev ? { ...prev, studies: studies as IStudyData[] } : prev));
	}, [studies, currentView]);

	const tableData = useMemo(() => {
		if (currentView === "folder_detail" && selectedFolder) {
			return selectedFolder.studies.map((study) => ({
				...study,
				isFolder: false,
				parentId: selectedFolder.folder_id,
				folder_id: selectedFolder.folder_id,
			}));
		}
		const result: any[] = [];
		folders.forEach((f) => result.push({ ...f, isFolder: true }));
		(studies as IStudyData[]).forEach((s) => result.push({ ...s, isFolder: false, parentId: null }));
		return result;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentView, selectedFolder, folders, studies]);

	const dataReady = !loading;
	const { startIndex, endIndex, paddingTop, paddingBottom, scrollContainerRef } = useVirtualRows(tableData, tbodyAnchorRef, ROW_HEIGHT, dataReady);

	// Sig is keyed on item IDs (not array ref) so polling refreshes don't reset scroll.
	const tableDataSig = useMemo(() => {
		const ids = tableData.map((item) => (item.isFolder ? `f:${item.folder_id}` : `s:${item.study_id}`));
		return `${currentView}|${selectedFolder?.folder_id ?? ""}|${ids.join(",")}`;
	}, [tableData, currentView, selectedFolder]);

	useEffect(() => {
		if (scrollContainerRef.current) {
			scrollContainerRef.current.scrollTop = 0;
		}
	}, [tableDataSig, scrollContainerRef]);

	const virtualDataSource = useMemo(() => {
		if (tableData.length <= INITIAL_ROWS) return tableData;
		return tableData.slice(startIndex, endIndex);
	}, [tableData, startIndex, endIndex]);

	const checkboxState = useMemo(() => {
		const source = currentView === "folder_detail" && selectedFolder ? selectedFolder.studies : allUntrackedStudies;
		const selectable = source.filter((s) => s.user_access_type === userAccessType.edit);
		return {
			isAllSelected: selectable.length > 0 && selectedCount === selectable.length,
			isIndeterminate: selectedCount > 0 && selectedCount < selectable.length,
		};
	}, [currentView, selectedFolder, allUntrackedStudies, selectedCount]);

	const currentFolderId = useMemo(() => {
		if (selectedCount === 0) return null;
		const firstStudyId = Object.keys(selectedStudiesMap)[0];
		const firstStudy = tableData.find((item) => !item.isFolder && item.study_id === firstStudyId);
		return firstStudy?.folder_id || null;
	}, [selectedStudiesMap, selectedCount, tableData]);

	// Prune selectedStudiesMap when filters (study type/search/collaborator) hide
	// previously-selected rows. Without this, selectedCount and the "select all"
	// checkbox state go stale because the map still references IDs no longer in tableData.
	const prevCurrentViewRef = useRef(currentView);
	useEffect(() => {
		// Skip pruning across folder navigation transitions (list <-> folder_detail);
		// tableData swaps wholesale and selections should be preserved across that change.
		if (prevCurrentViewRef.current !== currentView) {
			prevCurrentViewRef.current = currentView;
			return;
		}

		const currentIds = Object.keys(selectedStudiesMapRef.current);
		if (currentIds.length === 0) return;

		const visibleStudyIds = new Set<string>();
		for (const item of tableData) {
			if (!item.isFolder) visibleStudyIds.add(item.study_id);
		}

		const prunedIds = currentIds.filter((id) => visibleStudyIds.has(id));
		if (prunedIds.length === currentIds.length) return;

		const next: Record<string, boolean> = {};
		for (const id of prunedIds) next[id] = true;
		setSelectedStudiesMap(next);
	}, [tableData, currentView]);

	useEffect(() => {
		setFolderClickHandler?.(handleFolderClick);
	}, [setFolderClickHandler, handleFolderClick]);

	const getStudyTypeIcon = (studyType: string, types: any[]): StudyTypeIconDescriptor | null => {
		const matchedType = types?.find((type) => type?.label === studyType || type?.key === studyType);
		return matchedType?.icon2 ?? null;
	};

	// ─── Columns (original, unchanged) ───────────────────────────────────────
	const columns = useMemo(() => {
		const allColumns = [
			{
				title: "",
				dataIndex: "checkbox",
				key: "checkbox",
				width: "1.8%",
				render: (_: any, record: any) => (
					<CheckboxCell record={record} isFolderEnabled={isFolderEnabled} onChange={handleCheckboxChange} />
				),
			},
			{
				title: t("Contents"),
				dataIndex: "name",
				key: "name",
				width: "49%",
				ellipsis: true,
				render: (_: any, record: any) => {
					if (record.isFolder) {
						const studyCount = record?.studies?.length || 0;
						const starred = record?.is_star ?? false;
						return (
							<div className="flex items-center justify-start gap-5">
								<div onClick={(e) => handleToggleStar(e, record)} className="collab-table-star-icon flex-shrink-0">
									{starred ? (
										<i className="material-symbols-outlined filled-icon text-[--secondary-color-2] text-2xl">star</i>
									) : (
										<Tooltip getPopupContainer={(t) => t.closest(".studies-page-container") ?? document.body} title={t("Feature is disabled for collection")}>
											<i className="material-symbols-outlined text-[--secondary-color-opacity-30] cursor-not-allowed text-2xl">star</i>
										</Tooltip>
									)}
								</div>
								<div className="flex flex-col w-full min-w-0">
									<div
										className="collab-table-head-title flex items-center gap-2"
										style={{ maxWidth: "85%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
										<Folder className="flex-shrink-0" />
										<Tooltip
											getPopupContainer={(trigger) => trigger.closest(".studies-page-container") ?? document.body}
											title={
												<span>
													<span className="font-medium text-base-12">
														{formatMessage("{featureName} Name", t, featureName)}: {record.folder_name}
													</span>
													<br />
													{t("Description")}: {record.folder_description || ""}
												</span>
											}
											overlayInnerStyle={{ maxHeight: "200px", overflow: "auto" }}
											placement="right">
											<span className="truncate">
												{record.folder_name}{" "}
												<span className="text-[--secondary-color] font-semibold italic">
													({studyCount} {studyCount === 0 ? t("Study") : t("Studies")})
												</span>
											</span>
										</Tooltip>
									</div>
									<span className="collab-table-custom-author">
										{t("Updated")} {transformDateTime(record?.version_history?.modified_dt)}
									</span>
								</div>
							</div>
						);
					}
					const starred = record?.is_star ?? false;
					return (
						<div className="collab-table-name-container">
							<div className="flex items-center justify-start gap-5">
								<div onClick={(e) => handleToggleStar(e, record)} className="collab-table-star-icon flex-shrink-0">
									{starred ? (
										<i className="material-symbols-outlined filled-icon text-[--secondary-color-2] text-2xl">star</i>
									) : (
										<i className="material-symbols-outlined text-[--secondary-color-2] text-2xl">star</i>
									)}
								</div>
								<div className="flex flex-col w-full min-w-0">
									<div
										className="collab-table-head-title flex items-center gap-2"
										style={{ maxWidth: "85%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
										<StudyTypeIcon descriptor={getStudyTypeIcon(record?.study_type, allStudyTypes)} className="w-[18px]" />
										<Tooltip
											getPopupContainer={(trigger) => trigger.closest(".studies-page-container") ?? document.body}
											title={
												<span>
													<span className="font-medium text-base-12">
														{t("Study Name")}: {record.study_name}
													</span>
													<br />
													{t("Description")}: {record.description || ""}
												</span>
											}
											placement="right">
											<span className="truncate">{record.study_name}</span>
										</Tooltip>
									</div>
									<span className="custom-type">
										{isMultiLingualSupportEnabled && record?.study_type === storyType.type1 && (
											<Tooltip
												getPopupContainer={(trigger) => trigger.closest(".studies-page-container") ?? document.body}
												title={t("Study Language")}
												placement="bottom"
												overlayClassName="tooltip-reduced-padding">
												<Tag className="text-[x-small] text-[--secondary-color-2-darker] border-[--neutral-grey-opacity-15] bg-[--neutral-white]">
													{t(record?.study_language?.name || "English")}
												</Tag>
											</Tooltip>
										)}
										{t(getStudyTypeDisplayName(record.study_type, allStudyTypes))}
										<span className="border-l-2 border-[--secondary-color-opacity-25] h-4 mx-2"></span>
										<span className="collab-table-custom-author">
											{t("Updated")} {transformDateTime(record?.version_history?.modified_dt)}
										</span>
									</span>
								</div>
							</div>
						</div>
					);
				},
			},
			{
				title: t("Access"),
				dataIndex: "user_access_type",
				key: "user_access_type",
				width: "10%",
				render: (_: any, record: any) => {
					if (record.isFolder) return null;
					const accessType = record?.user_access_type;
					const isOwner = record?.collaborators?.some((c: any) => c.is_owner && c.collaborator_id === record?.version_history?.created_by);
					let displayText = "";
					if (accessType === userAccessType.edit) displayText = isOwner ? t("Study Owner") : t("Edit");
					else if (accessType === userAccessType.view) displayText = t("Read-Only");
					return <div className="text-base-14 font-normal text-[--neutral-grey-opacity-60]">{displayText}</div>;
				},
			},
			{
				title: t("Custom Tags"),
				dataIndex: "custom_tags",
				key: "custom_tags",
				width: "20%",
				render: (_: any, record: any) => <TagsCell record={record} />,
			},
			{
				title: t("Collaborators"),
				dataIndex: "collaborators",
				key: "collaborators",
				width: "12.5%",
				render: (_: any, record: any) => {
					if (record.isFolder) return null;
					const hasAccess = record?.user_access_type === userAccessType.edit || record?.user_access_type === userAccessType.view;
					return (
						<div className="flex items-center -space-x-1">
							<Avatar.Group size="small" max={{ count: 4 }} className="mix-blend-normal -z-0">
								{record.collaborators?.map((c: any) => (
									<Tooltip
										getPopupContainer={(trigger) => trigger.closest(".studies-page-container") ?? document.body}
										key={c.collaborator_id}
										title={c.collaborator_name}>
										<Avatar className={`${c.is_owner ? "owner-avatar" : ""} ${c.is_active === false ? "inactive-avatar" : ""}`}>
											{getInitials(c.collaborator_name)}
										</Avatar>
									</Tooltip>
								))}
							</Avatar.Group>
							{renderedFrom !== CollabCardRenderingFromType.AddStudy && (
								<Tooltip
									getPopupContainer={(trigger) => trigger.closest(".studies-page-container") ?? document.body}
									title={!hasAccess && t("Request existing collaborators to add more collaborators")}>
									<PlusCircleFilled
										className={`mix-blend-normal text-[1.4rem] z-0 ${hasAccess ? "collab-card-plus-icon" : "collab-card-plus-icon-disabled"}`}
										onClick={(e) => {
											e.stopPropagation();
											if (hasAccess) showAddCollabModal(e, record);
										}}
									/>
								</Tooltip>
							)}
						</div>
					);
				},
			},
			{
				title: t("Actions"),
				key: "action",
				width: "7.5%",
				align: "center",
				render: (_: any, record: any) => {
					if (record.isFolder) {
						if (!isFolderUpsert) return null;
						const hasFullAccess = record?.read_access_count === 0;
						return (
							<div className="flex items-center justify-center gap-2">
								<Dropdown
									menu={{
										items: [
											{
												key: "rename",
												label: t("Edit"),
												onClick: ({ domEvent }) => {
													domEvent.stopPropagation();
													handleFolderRename(record);
												},
											},
											{
												key: "delete",
												label: (
													<Tooltip
														getPopupContainer={(trigger) => trigger.closest(".studies-page-container") ?? document.body}
														title={!hasFullAccess ? t("You need edit access to all studies to delete this collection") : "Delete Collection"}
														placement="left">
														<span
															className={!hasFullAccess ? "text-[--neutral-grey-opacity-40]" : ""}
															onClick={(e) => {
																if (!hasFullAccess) {
																	e.stopPropagation();
																	e.preventDefault();
																}
															}}>
															{t("Delete")}
														</span>
													</Tooltip>
												),
												onClick: ({ domEvent }) => {
													domEvent.stopPropagation();
													domEvent.preventDefault();
													if (hasFullAccess) fetchDeleteOptions(record);
												},
												disabled: !hasFullAccess,
												className: !hasFullAccess ? "dropdown-item-disabled" : "",
											},
										],
									}}
									overlayClassName="chat-side-panel-dropdown"
									getPopupContainer={(trigger) => trigger.closest(".studies-page-container") ?? document.body}
									onOpenChange={(open) => setMoreMenuActiveFolderId(open ? record.folder_id : null)}
									trigger={["click"]}>
									<span className="more-icon-wrapper" onClick={(e) => e.stopPropagation()}>
										<i className="material-symbols-outlined secondary-color text-3xl align-middle">more_horiz</i>
									</span>
								</Dropdown>
							</div>
						);
					}
					const isReportsStudy = record?.study_type === storyType.type2;
					return (
						<div className="flex items-center justify-center gap-2">
							{isStudyUpsert && (
								<Button
									className="table-action-btn"
									type="text"
									onClick={(e) => handleManageClick(e, record)}
									disabled={renderedFrom === CollabCardRenderingFromType.AddStudy || renderedFrom === CollabCardRenderingFromType.ManageStudies}>
									<i className="material-symbols-outlined">page_info</i>
								</Button>
							)}
							{isReportsStudy ? (
								<>
									<span className="mx-1" style={{ width: "0.0833rem", height: "1.3333rem", display: "inline-block" }} />
									<span style={{ width: "2.3333rem", height: "2.3333rem", display: "inline-flex", alignItems: "center", justifyContent: "center" }} />
								</>
							) : (
								<>
									<Divider type="vertical" className="h-[1.33333rem] bg-[--secondary-color-2-opacity-15] mx-1" />
									<Button className="table-action-btn" type="text" onClick={(e) => handleInsightsClick(e, record)} disabled={!record?.insights_available}>
										<i className="material-symbols-outlined">emoji_objects</i>
									</Button>
								</>
							)}
						</div>
					);
				},
			},
		];
		return allColumns.filter((col) => !(col.key === "checkbox" && !isFolderEnabled));
	}, [
		handleCheckboxChange,
		handleToggleStar,
		isMultiLingualSupportEnabled,
		allStudyTypes,
		renderedFrom,
		showAddCollabModal,
		isStudyUpsert,
		handleManageClick,
		handleInsightsClick,
		isFolderUpsert,
		isFolderEnabled,
		handleFolderRename,
		fetchDeleteOptions,
		featureName,
		t,
	]);

	return (
		<SelectionContext.Provider value={selectedStudiesMap}>
			<>
				{/* 2. SELECTION BAR (Sticks at 184px / 11.5rem) */}
				{isFolderEnabled && selectedCount > 0 && (
					<div className="selection-bar sticky z-[15] flex items-center justify-between px-4 pl-[1.2rem] mb-2 bg-white" style={{ top: "173px" }}>
						<div className="flex items-center gap-4">
							<Checkbox checked={checkboxState.isAllSelected} indeterminate={checkboxState.isIndeterminate} onChange={(e) => handleSelectAll(e.target.checked)} />
							<span className="text-base-13 text-[--secondary-color]">
								{selectedCount} {t("study(s) selected (With edit access)")}
							</span>
						</div>
						<div className="flex items-center my-1">
							<Button
								type="text"
								icon={<i className="material-symbols-outlined secondary-color text-3xl">drive_file_move</i>}
								onClick={() => setIsMoveFolderModalOpen(true)}
								className={`flex items-center secondary-color text-base-13 ${showMoveButtonHighlight ? "button-highlight" : ""}`}>
								{formatMessage(`Move to {featureName}`, t, featureName)}
							</Button>
						</div>
					</div>
				)}

				{/* 1. CUSTOM SEPARATE HEADER (Sticks below Subheader or Selection Bar) */}
				<div
					className="sticky z-[5] border-[--secondary-color-2-opacity-10]"
					style={{ top: selectedCount > 0 ? "210px" : "170px", backgroundColor: "white", backgroundImage: "linear-gradient(var(--primary-color-opacity-10), var(--primary-color-opacity-10))" }}>
					<div className="flex items-center px-4" style={{ minWidth: "1000px" }}>
						{columns.map((col) => (
							<div
								key={col.key || col.dataIndex}
								style={{ width: col.width, flexShrink: 0, textAlign: col.align as any, padding: "12px 8px" }}
								className="text-base-14 font-semibold text-[--secondary-color] tracking-wider">
								{col.title}
							</div>
						))}
					</div>
				</div>

				<div
					ref={tbodyAnchorRef}
					className="collab-table-container table-checkbox-container"
					style={{ width: "100%", paddingTop, paddingBottom }}>
					<Table
						className="collab-table mb-5"
						columns={columns}
						dataSource={virtualDataSource}
						pagination={false}
						showHeader={false}
						rowKey={(record) => (record.isFolder ? record.folder_id : record.study_id)}
						scroll={{ x: "1000px", scrollToFirstRowOnChange: false }}
						loading={loading}
						bordered={false}
						tableLayout="fixed"
						rowClassName={(record) => {
							if (record.isFolder) return "folder-row cursor-pointer";
							return `study-row ${activeStudyId === record.study_id ? "row-active" : ""} ${selectedStudiesMap[record.study_id] ? "row-selected" : ""}`;
						}}
						onRow={(record) => ({
							onClick: () => {
								if (record.isFolder && currentView === "list") {
									handleFolderClick(record);
								} else if (!record.isFolder) {
									const index = tableData.findIndex((item) => !item.isFolder && item.study_id === record.study_id);
									onSelect(index);
								}
							},
						})}
					/>
				</div>

				<Suspense fallback={<></>}>
					{isAddCollabModalOpen && selectedStudyLocal && (
						<AddCollaboratorModal
							authorUser={selectedStudyLocal?.version_history?.created_by}
							fullAccessUsers={selectedStudyLocal?.collaborators}
							isModalOpen={isAddCollabModalOpen}
							handleOk={handleOk}
							handleCancel={handleCancel}
							key={collabModalKey}
							studyId={selectedStudyLocal?.study_id}
							studyName={selectedStudyLocal?.study_name}
							folderId={selectedStudyLocal?.folder_id}
							onRefreshStudies={onRefreshStudies}
						/>
					)}
					{isMoveFolderModalOpen && (
						<MoveFolderModal
							isOpen={isMoveFolderModalOpen}
							folders={folders}
							selectedStudiesCount={selectedCount}
							currentFolderId={currentFolderId}
							onMove={handleMoveToFolder}
							onCancel={() => setIsMoveFolderModalOpen(false)}
						/>
					)}
					{isDeleteModalOpen && selectedFolderToDelete && (
						<FolderDeleteModal
							isOpen={isDeleteModalOpen}
							folderName={selectedFolderToDelete.folder_name}
							deleteOptions={deleteOptions}
							loading={deletingFolder}
							onDelete={handleFolderDelete}
							onCancel={handleDeleteModalCancel}
						/>
					)}
				</Suspense>
			</>
		</SelectionContext.Provider>
	);
};

export default memo(CollabTable);